import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBoundary, NetworkStatusBanner } from '@/components/ErrorBoundary';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ThreePanelTaskPage from '@/pages/ThreePanelTaskPage';
import AllTasksListPage from '@/pages/AllTasksListPage';
import CreateTaskPage from '@/pages/CreateTaskPage';
import AdminDashboard from '@/pages/AdminDashboard';
import DesignersPage from '@/pages/DesignersPage';
import UserManagementPage from '@/pages/UserManagementPage';
import AuditLogPage from '@/pages/AuditLogPage';
import QuotationEnginePage from '@/pages/QuotationEnginePage';
import CADSheetPage from '@/pages/CADSheetPage';
import ManufacturingPage from '@/pages/ManufacturingPage';
import ProductDevAIPage from '@/pages/ProductDevAIPage';
import PortalImportPage from '@/pages/PortalImportPage';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/tasks" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return '/login';
    return '/tasks';
  };

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <ThreePanelTaskPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/all-tasks"
        element={
          <ProtectedRoute>
            <AllTasksListPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/tasks/new"
        element={
          <ProtectedRoute>
            <CreateTaskPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute adminOnly>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/quotation"
        element={
          <ProtectedRoute>
            <QuotationEnginePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/cad-sheets"
        element={
          <ProtectedRoute>
            <CADSheetPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/manufacturing"
        element={
          <ProtectedRoute>
            <ManufacturingPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/pd-ai"
        element={
          <ProtectedRoute>
            <ProductDevAIPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/portal-import"
        element={
          <ProtectedRoute>
            <PortalImportPage />
          </ProtectedRoute>
        }
      />
      
      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <NetworkStatusBanner />
          <AppRoutes />
          <Toaster position="top-right" expand={false} richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
